import { useEditorState, type Editor } from '@tiptap/react';
import { RedoIcon, UndoIcon } from 'lucide-react';
import { useCallback, useMemo } from 'react';

import { Button } from '@/components/ui/button/Button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useTiptapEditor } from '@/hooks/tiptap';

import type { HistoryAction, UndoRedoButtonProps } from '.';

const HISTORY_ICONS = {
  undo: UndoIcon,
  redo: RedoIcon,
};

const HISTORY_SHORTCUTS: Partial<Record<HistoryAction, string>> = {
  undo: 'Ctrl-z',
  redo: 'Ctrl-Shift-z',
};

const HISTORY_LABELS: Record<HistoryAction, string> = {
  undo: 'Cofnij',
  redo: 'Przywróć',
};

function canHistory(editor: Editor | null, action: HistoryAction): boolean {
  return (
    useEditorState({
      editor,
      selector: (ctx) => (action === 'undo' ? ctx.editor?.can().undo() : ctx.editor?.can().redo()),
    }) ?? false
  );
}

function runHistory(editor: Editor | null, action: HistoryAction): boolean {
  if (!editor) return false;
  const chain = editor.chain().focus();
  return action === 'undo' ? chain.undo().run() : chain.redo().run();
}
function useHistoryButton(editor: Editor | null, action: HistoryAction, userDisabled: boolean = false) {
  const canExecute: boolean = useMemo(() => canHistory(editor, action), [editor, action]);

  const isDisabled: boolean = userDisabled || !canExecute;

  const handleAction = useCallback(() => {
    if (!editor || isDisabled) return;
    runHistory(editor, action);
  }, [editor, action, isDisabled]);

  return {
    Icon: HISTORY_ICONS[action],
    label: HISTORY_LABELS[action],
    shortcut: HISTORY_SHORTCUTS[action],
    isDisabled,
    handleAction,
  };
}

function useUndoRedoButtonClick(onClick: UndoRedoButtonProps['onClick'], callback: () => void) {
  return useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(e);
      if (!e.defaultPrevented) {
        callback();
      }
    },
    [onClick, callback]
  );
}

export function UndoRedoButton(undoRedoButtonProps: UndoRedoButtonProps) {
  const { action, editor: providedEditor, disabled, onClick, children, ...props } = undoRedoButtonProps;

  const editor: Editor | null = useTiptapEditor(providedEditor);

  const { Icon, label, shortcut, isDisabled, handleAction } = useHistoryButton(editor, action, disabled);

  const handleClick = useUndoRedoButtonClick(onClick, handleAction);

  if (!editor || !editor.isEditable) {
    return null;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button role="button" data-slot="undo-redo-button" onClick={handleClick} disabled={isDisabled} {...props}>
          {children || <Icon />}
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        {label}
        {shortcut && <p className="text-xs opacity-70">{shortcut}</p>}
      </TooltipContent>
    </Tooltip>
  );
}
